/**
 * Sole allowed source for platform service URL paths in the portal.
 * Paths come from infrastructure/platform-routes.json → redirect-registry.json.
 * Do not hardcode /git, /openproject, /auth, etc. outside this module (and the
 * emit/allowlisted registry adapters). Use resolve* / join* by service id + role.
 */
import generatedRegistry from '../../generated/redirect-registry.json';

export const URL_ROLES = [
  'public',
  'launch',
  'sso_entry',
  'callback',
  'landing',
  'admin',
  'api',
  'service_login',
] as const;

export type UrlRole = (typeof URL_ROLES)[number];

export type ServiceId =
  | 'portal'
  | 'keycloak'
  | 'openproject'
  | 'gitea'
  | 'woodpecker'
  | 'nextcloud'
  | 'collabora'
  | 'vaultwarden'
  | 'netbird'
  | 'coturn';

/** Widget / catalog aliases → canonical registry service id. */
const SERVICE_ALIASES: Record<string, ServiceId> = {
  collabra: 'collabora',
};

interface RegistryEntry {
  id?: string;
  paths?: Partial<Record<UrlRole, string>>;
}

function originBase(origin: string): string {
  return origin.replace(/\/$/, '');
}

function normalizeServiceId(id: string): ServiceId {
  const mapped = SERVICE_ALIASES[id] ?? id;
  return mapped as ServiceId;
}

function entryById(id: string): RegistryEntry | undefined {
  const canonical = normalizeServiceId(id);
  const items = (generatedRegistry.entries ?? []) as RegistryEntry[];
  return items.find((e) => e.id === canonical);
}

export function listRegistryServiceIds(): ServiceId[] {
  const items = (generatedRegistry.entries ?? []) as RegistryEntry[];
  return items.map((e) => e.id as ServiceId).filter(Boolean);
}

export function getServicePaths(id: string): Partial<Record<UrlRole, string>> {
  return entryById(id)?.paths ?? {};
}

export function tryResolveServicePath(id: string, role: UrlRole): string | null {
  const path = getServicePaths(id)[role];
  return path ?? null;
}

export function resolveServicePath(id: string, role: UrlRole): string {
  const path = tryResolveServicePath(id, role);
  if (!path) {
    throw new Error(
      `[serviceUrlRegistry] no path for service='${normalizeServiceId(id)}' role='${role}' — add it to platform-routes.json services[].portal / sso and regenerate redirect-registry.json`
    );
  }
  return path;
}

export function resolveServiceUrl(id: string, role: UrlRole, origin: string): string {
  const path = resolveServicePath(id, role);
  if (path.startsWith('http://') || path.startsWith('https://')) {
    return path;
  }
  const base = originBase(origin);
  return `${base}${path.startsWith('/') ? path : `/${path}`}`;
}

export function tryResolveServiceUrl(
  id: string,
  role: UrlRole,
  origin: string
): string | null {
  const path = tryResolveServicePath(id, role);
  if (!path) return null;
  if (path.startsWith('http://') || path.startsWith('https://')) {
    return path;
  }
  const base = originBase(origin);
  return `${base}${path.startsWith('/') ? path : `/${path}`}`;
}

function joinSegments(basePath: string, segments: string[]): string {
  let out = basePath.replace(/\/$/, '') || '';
  for (const seg of segments) {
    if (!seg && seg !== '0') continue;
    const clean = String(seg).replace(/^\/+|\/+$/g, '');
    if (!clean) continue;
    out = `${out}/${clean}`;
  }
  return out.startsWith('/') ? out : `/${out}`;
}

/** Deep link under a registry role base, e.g. joinServicePath('gitea','public','org','repo'). */
export function joinServicePath(id: string, role: UrlRole, ...segments: string[]): string {
  return joinSegments(resolveServicePath(id, role), segments);
}

export function joinServiceUrl(
  id: string,
  role: UrlRole,
  origin: string,
  ...segments: string[]
): string {
  const path = joinServicePath(id, role, ...segments);
  return `${originBase(origin)}${path}`;
}

/** Prefixes that must not appear as hardcoded path literals outside allowlisted modules. */
export const FORBIDDEN_PLATFORM_PATH_RE =
  /\/(?:git|gitea|openproject|nextcloud|vault|ci|auth|mesh|collabora|collabra|launch)\b/;

/** Edge aliases that redirect into a registry public path (gitea_root → /git). */
export const LEGACY_PUBLIC_PATH_ALIASES = ['/gitea'] as const;

/** React Router pattern for ARCH-011 portal launch (entry_path = /launch/:serviceId). */
export function portalLaunchRoutePath(): string {
  return '/launch/:serviceId';
}
